import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Icon, Label, Loader, Popup } from 'semantic-ui-react';
import { useAuth } from '../../context/AuthContext';
import { posVendaQueryKeys } from '../../hooks/posVenda/posVendaQueryKeys';
import posVendaService from '../../services/posVenda/PosVendaService';
import {
  PosVendaProtocoloListItemDto,
  PosVendaProtocoloStatus,
  PosVendaProtocolosFiltros,
} from '../../types/posVenda';
import { SLA_ESTADOS_TODOS, slaEstadoCanonicoFromApi } from '../../utils/posVenda/posVendaSlaEstado';
import {
  PROTOCOLO_STATUS_ATIVOS,
  PROTOCOLO_STATUS_LABEL,
  labelTipoProtocolo,
} from '../../utils/posVenda/posVendaLabels';
import SlaBadge from '../../pages/PosVenda/components/SlaBadge';
import { ClientePainelDrawer } from '../../pages/PosVenda/components/ClientePainelDrawer';

const STALE_MS = 60_000;
const PAGE_SIZE = 100;
/** A API serializa enums em camelCase (`aberto`, `emAnalise`). O painel compara em PascalCase. */
function chaveIgnorandoCaixa<T extends string>(valor: string | null | undefined, chaves: readonly T[]): T | undefined {
  const alvo = (valor ?? '').toLowerCase();
  return chaves.find((chave) => chave.toLowerCase() === alvo);
}

function nomeCliente(item: PosVendaProtocoloListItemDto): string {
  return item.clienteRazaoSocial || item.clientRazaoSocial || 'Cliente';
}

/**
 * Atalho flutuante dos protocolos abertos atribuídos ao usuário.
 * A lista só é buscada quando o painel abre (staleTime de 60s, sem polling).
 */
const FloatingPosVendaTickets: React.FC = () => {
  const { user, isAuthenticated, hasRole } = useAuth();
  const podeVer = isAuthenticated && hasRole('Admin');
  const [painelAberto, setPainelAberto] = useState(false);
  const [protocoloAberto, setProtocoloAberto] = useState<PosVendaProtocoloListItemDto | null>(null);
  const userId = user?.id ?? '';

  const filtros = useMemo<PosVendaProtocolosFiltros>(
    () => ({
      responsavelUserId: userId,
      page: 1,
      pageSize: PAGE_SIZE,
    }),
    [userId]
  );

  const protocolosQuery = useQuery({
    queryKey: posVendaQueryKeys.protocolos(filtros),
    queryFn: () => posVendaService.listProtocolos(filtros),
    enabled: painelAberto && podeVer && userId.length > 0,
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const abertos = useMemo(() => {
    const id = userId.toLowerCase();
    const items = (protocolosQuery.data?.items ?? []) as PosVendaProtocoloListItemDto[];
    return items.filter((item) => {
      const status = chaveIgnorandoCaixa(item.status, PROTOCOLO_STATUS_ATIVOS);
      return status != null && (item.responsavelUserId ?? '').toLowerCase() === id;
    });
  }, [protocolosQuery.data, userId]);

  if (!podeVer) return null;

  const abrirProtocolo = (item: PosVendaProtocoloListItemDto) => {
    setPainelAberto(false);
    setProtocoloAberto(item);
  };

  const badge = protocolosQuery.data && abertos.length > 0 ? abertos.length : 0;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '96px',
          zIndex: 1000,
        }}
      >
        <Popup
          content="Meus tickets do pós-venda"
          position="left center"
          trigger={
            <Button
              type="button"
              circular
              icon="ticket"
              aria-label="Meus tickets do pós-venda"
              aria-expanded={painelAberto}
              onClick={() => setPainelAberto((aberto) => !aberto)}
              style={{
                width: '40px',
                height: '40px',
                minWidth: '40px',
                padding: 0,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                background: '#0d9488',
                color: '#fff',
              }}
            />
          }
        />
        {badge > 0 && (
          <Label
            color="red"
            circular
            size="mini"
            style={{
              position: 'absolute',
              top: '-6px',
              right: '-6px',
              margin: 0,
            }}
          >
            {badge > 99 ? '99+' : badge}
          </Label>
        )}
      </div>

      {painelAberto && (
        <div
          role="dialog"
          aria-label="Tickets abertos do pós-venda"
          style={{
            position: 'fixed',
            right: '96px',
            bottom: '72px',
            width: 'min(340px, calc(100vw - 120px))',
            maxHeight: '420px',
            zIndex: 1001,
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
            border: '1px solid rgba(34, 36, 38, 0.15)',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              padding: '8px 10px',
              borderBottom: '1px solid #e8e8e8',
              background: '#f8fafc',
            }}
          >
            <strong style={{ fontSize: '13px' }}>
              <Icon name="ticket" />
              Meus tickets
              {protocolosQuery.data ? ` (${abertos.length})` : ''}
            </strong>
            <Button
              type="button"
              icon
              basic
              size="mini"
              aria-label="Fechar tickets do pós-venda"
              onClick={() => setPainelAberto(false)}
            >
              <Icon name="close" />
            </Button>
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {protocolosQuery.isLoading && (
              <div style={{ padding: '16px', textAlign: 'center' }}>
                <Loader active inline size="small" />
              </div>
            )}
            {protocolosQuery.isError && (
              <p style={{ margin: '12px', color: '#9f3a38', fontSize: '13px' }}>
                Não foi possível carregar os tickets.
              </p>
            )}
            {protocolosQuery.data && abertos.length === 0 && (
              <p style={{ margin: '12px', color: '#667', fontSize: '13px' }}>
                Nenhum protocolo aberto atribuído a você.
              </p>
            )}
            {abertos.map((item) => {
              const status =
                chaveIgnorandoCaixa<PosVendaProtocoloStatus>(
                  item.status,
                  Object.keys(PROTOCOLO_STATUS_LABEL) as PosVendaProtocoloStatus[]
                ) ?? item.status;
              const slaEstado = slaEstadoCanonicoFromApi(item.slaEstado);
              return (
              <button
                key={item.id}
                type="button"
                onClick={() => abrirProtocolo(item)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  border: 'none',
                  borderBottom: '1px solid #f0f0f0',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <strong style={{ fontSize: '13px' }}>{item.numero}</strong>
                  <span style={{ fontSize: '12px', color: '#555' }}>
                    {PROTOCOLO_STATUS_LABEL[status] ?? status}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#333',
                    marginTop: '2px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {nomeCliente(item)}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginTop: '4px',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: '12px', color: '#666' }}>{labelTipoProtocolo(item.tipo)}</span>
                  <SlaBadge
                    estado={slaEstado}
                    percentual={item.slaPercentualConsumido}
                    status={status}
                  />
                </div>
              </button>
              );
            })}
          </div>
        </div>
      )}

      <ClientePainelDrawer
        visible={protocoloAberto != null}
        clientId={protocoloAberto?.clientId ?? null}
        protocoloId={protocoloAberto?.id ?? null}
        isGestor={podeVer}
        onClose={() => setProtocoloAberto(null)}
      />
    </>
  );
};

export default FloatingPosVendaTickets;

